import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FooterLinkManager from '../../components/admin/FooterLinkManager';
import OfficeManager from '../../components/admin/OfficeManager';

interface Setting {
  value: any;
  type: string;
  display_name: string;
  description: string;
}

interface SettingsData {
  [key: string]: Setting;
}

interface CategorySettings {
  [category: string]: SettingsData;
}

interface Office {
  country: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  flag: string;
  timezone: string;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState<CategorySettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const tabs = [
    { id: 'general', name: 'General Settings', icon: '⚙️' },
    { id: 'contact', name: 'Contact Information', icon: '📞' },
    { id: 'statistics', name: 'Statistics', icon: '📊' },
    { id: 'social', name: 'Social Media', icon: '🌐' },
    { id: 'footer', name: 'Footer Settings', icon: '🦶' },
    { id: 'system', name: 'System Settings', icon: '🔧' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('https://patrixglobal.com/backend/api/admin/settings/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Check if response is empty or not JSON
      const text = await response.text();
      if (!text) {
        setMessage({ type: 'error', text: 'Empty response from server' });
        return;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {        setMessage({ type: 'error', text: 'Invalid JSON response from server' });
        return;
      }

      if (data.success) {
        setSettings(data.data);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to load settings' });
      }
    } catch (error) {      const errorMessage = error instanceof Error ? error.message : 'Failed to load settings';

      if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
        setMessage({ type: 'error', text: 'Backend API not ready. Please ensure database tables are created and backend files are uploaded.' });
      } else {
        setMessage({ type: 'error', text: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (category: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: {
          ...prev[category][key],
          value: value
        }
      }
    }));
  };

  const handleOfficeChange = (officeKey: string, field: string, value: string) => {
    const currentOffices = settings.contact?.offices?.value || {};
    const updatedOffices = {
      ...currentOffices,
      [officeKey]: {
        ...currentOffices[officeKey],
        [field]: value
      }
    };

    handleSettingChange('contact', 'offices', updatedOffices);
  };

  const handleSave = async (category: string) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const categorySettings = settings[category];

      // Convert settings to the format expected by the API
      const settingsToSave: { [key: string]: { value: any } } = {};
      Object.keys(categorySettings).forEach(key => {
        settingsToSave[key] = { value: categorySettings[key].value };
      });

      const response = await fetch('https://patrixglobal.com/backend/api/admin/settings/', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: category,
          settings: settingsToSave
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save settings' });
      }
    } catch (error) {      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }

    // Clear message after 3 seconds
    setTimeout(() => setMessage(null), 3000);
  };

  const renderInput = (category: string, key: string, setting: Setting) => {
    const { value, type } = setting;

    switch (type) {
      case 'boolean':
        return (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleSettingChange(category, key, e.target.checked)}
              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <span className="ml-2">Enable</span>
          </label>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleSettingChange(category, key, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleSettingChange(category, key, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        );

      case 'color':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={value}
              onChange={(e) => handleSettingChange(category, key, e.target.value)}
              className="h-10 w-16 rounded border-gray-300 shadow-sm"
            />
            <input
              type="text"
              value={value}
              onChange={(e) => handleSettingChange(category, key, e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="#000000"
            />
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleSettingChange(category, key, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        );
    }
  };

  const renderOfficeSettings = () => {
    // Use the new OfficeManager component
    return <OfficeManager />;
  };

  const renderFooterTab = () => {
    const footerSettings = settings.footer;

    if (!footerSettings) {
      return <div>Loading footer settings...</div>;
    }

    return (
      <div className="space-y-8">
        {/* Visibility Controls Section */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Visibility Controls</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(footerSettings)
              .filter(key => key.includes('_enabled') || key === 'show_legal_links')
              .map(key => {
                const setting = footerSettings[key];
                return (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {setting.display_name}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">{setting.description}</p>
                    {renderInput('footer', key, setting)}
                  </div>
                );
              })}
          </div>
        </div>

        {/* Content Settings Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Settings</h3>
          <div className="space-y-4">
            {Object.keys(footerSettings)
              .filter(key =>
                (key.includes('_en') || key.includes('_ar') || key.includes('email')) &&
                !key.includes('_enabled') &&
                key !== 'show_legal_links'
              )
              .map(key => {
                const setting = footerSettings[key];
                return (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700">
                      {setting.display_name}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">{setting.description}</p>
                    {key.includes('description') ? (
                      <textarea
                        value={setting.value}
                        onChange={(e) => handleSettingChange('footer', key, e.target.value)}
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    ) : (
                      renderInput('footer', key, setting)
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {/* Design Settings Section */}
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Design Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(footerSettings)
              .filter(key => key.includes('color') || key.includes('gradient'))
              .map(key => {
                const setting = footerSettings[key];
                return (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700">
                      {setting.display_name}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">{setting.description}</p>
                    {renderInput('footer', key, setting)}
                  </div>
                );
              })}
          </div>
        </div>

        {/* Footer Links Management */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Footer Links Management</h3>

          <div className="space-y-6">
            <div className="border rounded-lg p-4">
              <FooterLinkManager linkType="quick_link" title="Quick Links" />
            </div>

            <div className="border rounded-lg p-4">
              <FooterLinkManager linkType="legal_link" title="Legal Links" />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-6 border-t">
          <button
            onClick={() => handleSave('footer')}
            disabled={saving}
            className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              'Save Footer Settings'
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    // Special handling for footer tab
    if (activeTab === 'footer') {
      return renderFooterTab();
    }

    const categorySettings = settings[activeTab];

    if (!categorySettings) {
      return <div>No settings available for this category.</div>;
    }

    return (
      <div className="space-y-6">
        {activeTab === 'contact' && settings.contact?.offices ? (
          <>
            {/* Regular contact settings */}
            {Object.keys(categorySettings).filter(key => key !== 'offices').map((key) => {
              const setting = categorySettings[key];
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700">
                    {setting.display_name}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">{setting.description}</p>
                  {renderInput(activeTab, key, setting)}
                </div>
              );
            })}

            {/* Office settings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Office Locations
              </label>
              {renderOfficeSettings()}
            </div>
          </>
        ) : (
          Object.keys(categorySettings).map((key) => {
            const setting = categorySettings[key];
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700">
                  {setting.display_name}
                </label>
                <p className="text-xs text-gray-500 mb-2">{setting.description}</p>
                {renderInput(activeTab, key, setting)}
              </div>
            );
          })
        )}

        <div className="pt-6 border-t">
          <button
            onClick={() => handleSave(activeTab)}
            disabled={saving}
            className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" dir="ltr">
        {/* Message */}
        {message && (
          <div className={`rounded-md p-4 ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Settings;